import { useEffect, useState } from 'react';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { IService } from '../../../../interfaces/service';
import type { IServiceGetOne } from '../../../../interfaces/serviceGetOne';
import type { IItemState } from '../../../episodes/Episode';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ServiceCode } from '../../../../../../common/enums/serviceCode';
import { fetchJson } from '../../../../../store/fetch';
import { initialConsultRequestFieldsForOrthO } from '../../OrthO/orthOV1ProcedureInitializeCustomControlData';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { OrthCV1RequestAddEditInner } from './OrthCV1RequestAddEditInner';

interface IOrthCV1RequestAddEditForOrthOProps {
    consultData?: IOrthCV1RequestAddEditConsultData;
    errors: ValidationErrors;
    handleChangeCustomControl: <T = IOrthCV1RequestAddEditConsultData>(values: Partial<T>) => Promise<void>;
    isFirstItem: boolean;
    itemState?: IItemState;
    service: IServiceGetOne;
    validateOneCustomControl: (name: string) => void;
}

export interface IOrthCV1RequestAddEditConsultData {
    question: string | null;
    selectedDiagnosticReportProviderOrganizationId: string | null;
    selectedDiagnosticReportProviderPractitionerId: string | null;
    toelichting: string | null;
}

export function OrthCV1RequestAddEditForOrthO(props: IOrthCV1RequestAddEditForOrthOProps) {
    const [orthCService, setOrthCService] = useState<IService>();

    useEffect(() => {
        loadOrthCService();
        setInitialData();
    }, []);

    if (!orthCService || !props.consultData) {
        return null;
    }

    return (
        <NewOrEditRequestProcessContainer
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            service={props.service}
            title={getServiceLabel(orthCService.code, 'request', orthCService.name)}
        >
            <OrthCV1RequestAddEditInner
                errors={transformValidationErrors(props.errors)}
                question={getPropsForAddEditInner('question')}
                selectedDiagnosticReportProvider={getSelectedDiagnosticReportProviderPropsForAddEditInner()}
                serviceId={orthCService!.id}
                toelichting={getPropsForAddEditInner('toelichting')}
            />
        </NewOrEditRequestProcessContainer>
    );

    async function loadOrthCService() {
        const result = await fetchJson(`/api/services?code=${ServiceCode.OrthC}`, 'GET');
        if (result.json.items) {
            setOrthCService(result.json.items[0]);
        } else {
            throw new Error('Cannot find OrthC service');
        }
    }

    async function setInitialData() {
        if (!props.consultData) {
            props.handleChangeCustomControl({ ...initialConsultRequestFieldsForOrthO, hasConsultRequestFields: true });
        }
    }

    async function handleConsultChange<K extends keyof IOrthCV1RequestAddEditConsultData>(
        name: K,
        value: string | null
    ) {
        await props.handleChangeCustomControl({
            consult: {
                ...props.consultData,
                [name]: value,
            },
        });
    }

    function transformValidationErrors(errors: ValidationErrors) {
        if (errors.has('customControlData.consult.selectedDiagnosticReportProviderPractitionerId')) {
            errors = errors.set('selectedDiagnosticReportProviderPractitionerId', 'any_required');
        } else {
            errors = errors.remove('selectedDiagnosticReportProviderPractitionerId');
        }

        if (errors.has('customControlData.consult.selectedDiagnosticReportProviderOrganizationId')) {
            errors = errors.set('selectedDiagnosticReportProviderOrganizationId', 'any_required');
        } else {
            errors = errors.remove('selectedDiagnosticReportProviderOrganizationId');
        }

        return errors;
    }

    function getSelectedDiagnosticReportProviderPropsForAddEditInner() {
        return {
            selectedDiagnosticReportProviderPractitionerId:
                props.consultData?.selectedDiagnosticReportProviderPractitionerId || null,
            selectedDiagnosticReportProviderOrganizationId:
                props.consultData?.selectedDiagnosticReportProviderOrganizationId || null,
            handleChange: async (organizationId: string | null, practitionerId: string | null) => {
                await props.handleChangeCustomControl({
                    consult: {
                        ...props.consultData,
                        selectedDiagnosticReportProviderOrganizationId: organizationId,
                        selectedDiagnosticReportProviderPractitionerId: practitionerId,
                    },
                });
            },
            validateOne: () => {
                props.validateOneCustomControl('consult.selectedDiagnosticReportProviderOrganizationId');
                props.validateOneCustomControl('consult.selectedDiagnosticReportProviderPractitionerId');
            },
        };
    }

    function getPropsForAddEditInner(consultDataField: keyof IOrthCV1RequestAddEditConsultData) {
        return {
            value: (props.consultData && props.consultData[consultDataField]) || null,
            error: props.errors.get(`customControlData.consult.${consultDataField}`),
            handleChange: async (value: string | null) => await handleConsultChange(consultDataField, value),
            validateOne: () => props.validateOneCustomControl(`consult.${consultDataField}`),
        };
    }
}
